import classNames from 'classnames';
import {
  FC, ReactNode, Dispatch, SetStateAction,
} from 'react';
import { useField } from 'react-final-form';
import getClassNames from 'src/common/utils/getClassNames';
import Icon from 'src/common/components/IconDS22';
import InputError from 'src/admin/common/components/InputError';
import PasswordVisibilityToggle from 'src/common/components/PasswordVisibilityToggle';
import styles from './InputWrapper.module.scss';

export type InputWrapperVariants =
| 'disabled'
| 'email'
| 'error'
| 'fullWidth'
| 'hidden'
| 'textEditor';

type BaseProps = {
  children: ReactNode;
  name: string;
  showIcon?: boolean;
  showErrorOnTouch?: boolean;
  /**
   * Show warning icon next to error message
   */
  showErrorMessageIcon?: boolean;
  highlightBorder?: boolean;
  preventErrorMessageLayoutShift?: boolean;
  isPasswordVisible?: boolean;
  setIsPasswordVisible?: Dispatch<SetStateAction<boolean>>;
  errorMessagePosition?: 'center';
  /**
   * The error message forcefully to show instead of the
   *  error message from the form.
   */
  errorMessage?: ReactNode;
};

type WithClassNameModifier = BaseProps & {
  /**
   * @deprecated Use 'variant' prop instead
   */
  classNameModifier?: InputWrapperVariants;
  variant?: never;
};

type WithVariant = BaseProps & {
  variant?: InputWrapperVariants;
  /**
 * @deprecated Use 'variant' prop instead
 */
  classNameModifier?: never;
};

type Props = WithClassNameModifier | WithVariant;
const InputWrapper: FC<Props> = ({
  children,
  name,
  classNameModifier = '',
  variant,
  showIcon = false,
  showErrorMessageIcon = false,
  highlightBorder = true,
  /**
   * Show error message only after user touched the field.
   */
  showErrorOnTouch = false,
  preventErrorMessageLayoutShift = true,
  isPasswordVisible = false,
  setIsPasswordVisible,
  errorMessagePosition,
  errorMessage = '',
}) => {
  const {
    meta: {
      dirtySinceLastSubmit, error, submitError, touched,
    },
  } = useField(name);
  const hasError = showErrorOnTouch ? !!error && !!touched : !!error;
  const hasSubmitError = !dirtySinceLastSubmit && submitError;

  const formError = error || submitError;
  const showErrorForcefully = !!errorMessage;
  const errorToShow = showErrorForcefully ? errorMessage : formError;
  return (
    <div
      className={getClassNames(
        styles,
        'root',
        classNames(
          {
            error: (hasError || hasSubmitError || showErrorForcefully) && highlightBorder,
          },
          classNameModifier,
          variant,
        ),
      )}
    >
      {children}
      {(hasError || hasSubmitError || showErrorForcefully) && (
        <>
          {showIcon && (
            <Icon
              id="warning-filled"
              className={getClassNames(styles, 'icon', classNameModifier)}
            />
          )}
          <InputError
            preventLayoutShift={preventErrorMessageLayoutShift}
            showErrorMessageIcon={showErrorMessageIcon}
            position={errorMessagePosition}
          >
            {errorToShow}
          </InputError>
        </>
      )}
      {setIsPasswordVisible && !hasError && !hasSubmitError && (
        <PasswordVisibilityToggle
          isPasswordVisible={isPasswordVisible}
          setIsPasswordVisible={setIsPasswordVisible}
        />
      )}
    </div>
  );
};

export default InputWrapper;
